import * as d3 from 'd3';
import { useEffect, useRef } from 'react';

const midAngle = (d) => d.startAngle + (d.endAngle - d.startAngle) / 2;

const PieChart = ({ data, widthInitial }) => {
  const svgRef = useRef();
  const wrapperRef = useRef();

  useEffect(() => {
    const width = widthInitial ?? 300;
    const height = Math.min(width, 200);
    const outerRadius = Math.min(width, height) / 2 - 1;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const color = d3
      .scaleOrdinal()
      .domain(data.map((d) => d.name))
      .range(['#a4c2d8', '#d8b4a4', '#a4c8a8', '#c4b8d8', '#d8cca4', '#b4d8c8']);

    const pie = d3.pie().sort(null).value((d) => d.value);
    const arc = d3.arc().innerRadius(0).outerRadius(outerRadius);
    const arcMid = d3.arc().innerRadius(outerRadius * 0.7).outerRadius(outerRadius * 0.7);
    const arcOuter = d3.arc().innerRadius(outerRadius + 18).outerRadius(outerRadius + 18);

    const arcs = pie(data);
    const total = data.reduce((acc, cur) => acc + cur.value, 0);

    svg
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [-width / 2, -height / 2, width, height])
      .attr('style', 'max-width: 100%; height: auto; font: 10px sans-serif; overflow: visible;');

    // Sectores
    svg
      .append('g')
      .attr('stroke', 'white')
      .selectAll()
      .data(arcs)
      .join('path')
      .attr('fill', (d) => color(d.data.name))
      .attr('d', arc)
      .append('title')
      .text((d) => `${d.data.name}: ${d.data.value.toLocaleString('en-US')}`);

    // Números dentro
    const arcInner = d3.arc().innerRadius(outerRadius * 0.62).outerRadius(outerRadius * 0.62);
    svg
      .append('g')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('font-size', 12)
      .attr('font-weight', '600')
      .attr('fill', '#333')
      .selectAll()
      .data(arcs)
      .join('text')
      .attr('transform', (d) => `translate(${arcInner.centroid(d)})`)
      .text((d) =>
        Intl.NumberFormat('es-MX', { style: 'percent', maximumFractionDigits: 1 }).format(
          d.data.value / total,
        ),
      );

    // Líneas conectoras
    svg
      .append('g')
      .attr('fill', 'none')
      .attr('stroke', '#bbb')
      .attr('stroke-width', 0.8)
      .selectAll()
      .data(arcs)
      .join('polyline')
      .attr('points', (d) => {
        const posA = arcMid.centroid(d);
        const posB = arcOuter.centroid(d);
        const posC = [...arcOuter.centroid(d)];
        posC[0] = (outerRadius + 22) * (midAngle(d) < Math.PI ? 1 : -1);
        return [posA, posB, posC].map((p) => p.join(',')).join(' ');
      });

    // Labels fuera
    svg
      .append('g')
      .attr('font-size', 9)
      .selectAll()
      .data(arcs)
      .join('text')
      .attr('transform', (d) => {
        const pos = arcOuter.centroid(d);
        pos[0] = (outerRadius + 24) * (midAngle(d) < Math.PI ? 1 : -1);
        return `translate(${pos})`;
      })
      .attr('text-anchor', (d) => (midAngle(d) < Math.PI ? 'start' : 'end'))
      .attr('dominant-baseline', 'middle')
      .call((text) =>
        text.append('tspan').attr('font-weight', 'bold').text((d) => d.data.name),
      )
      .call((text) =>
        text
          .append('tspan')
          .attr('x', 0)
          .attr('dy', '1.3em')
          .attr('fill-opacity', 0.6)
          .text((d) =>
            Intl.NumberFormat('es-MX', { style: 'percent', maximumFractionDigits: 1 }).format(
              d.data.value / total,
            ),
          ),
      );
  }, [data, widthInitial]);

  return (
    <div ref={wrapperRef} style={{ margin: '0rem' }}>
      <svg ref={svgRef} style={{ overflow: 'visible' }}></svg>
    </div>
  );
};

export default PieChart;
